import {
    faAngleRight,
    faBadgeCheck,
    faComment,
    faThumbsUp,
} from "@fortawesome/pro-regular-svg-icons";
import { faShare } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { History } from "history";
import React from "react";

import { BreadcrumbInput } from "@/mk2/apps/blogs/components/ArticleHeader";
import { ResizedPhoto } from "@/mk2/apps/blogs/components/BlogPostPhotos";
import CommentRepliedToMessage from "@/mk2/apps/blogs/components/CommentRepliedToMessage";
import { blogsProfileUrl, blogsUrl } from "@/mk2/apps/blogs/urls";
import { contestsHomeUrl } from "@/mk2/apps/contests/urls";
import {
    counsellingsHomeUrl,
    counsellingsNewUrl,
    counsellingsTopicUrl,
} from "@/mk2/apps/counsellings/urls";
import { UserChip } from "@/mk2/apps/forum/components/UserChip";
import { forumTopicUrl, forumTopicsUrl, forumUrl } from "@/mk2/apps/forum/urls";
import { HomePostEntity, HomePostType } from "@/mk2/apps/home/schemas";
import {
    extractFromSnippet,
    extractFromUrl,
    getPhotoVersionFactory,
} from "@/mk2/apps/wiki/helpers";
import { getWikiArticleUrl, wikiAddExperienceUrl } from "@/mk2/apps/wiki/urls";
import {
    RenderSnippetAs,
    constructInstagram,
    constructTiktok,
    constructVimeo,
    constructYoutube,
} from "@/mk2/apps/wiki/utils";
import { Btn, BtnSidePadding, BtnType } from "@/mk2/components/Btn";
import { Clamp } from "@/mk2/components/Clamp";
import { ImgCropMode, ImgCropped } from "@/mk2/components/ImgCropped";
import { Link } from "@/mk2/components/Link";
import { LoadingPlaceholder } from "@/mk2/components/LoadingPlaceholder";
import { OverlayLoadable } from "@/mk2/components/OverlayLoadable";
import { Panel } from "@/mk2/components/Panel";
import { StarsRating } from "@/mk2/components/StarsRating";
import { Tooltip } from "@/mk2/components/Tooltip";
import Like, { LikeType } from "@/mk2/containers/Like/Like";
import { Star } from "@/mk2/containers/Star/Star";
import { timeAt } from "@/mk2/helpers/timeformat";
import { url } from "@/mk2/helpers/urls";
import useOverlay from "@/mk2/hooks/useOverlay";
import { ExperienceVerificationStatus, UserEntity } from "@/mk2/schemas";
import { interpolate } from "@/mk2/services/i18n";
import {
    ADD_REVIEW,
    BLOGS,
    FOLLOW_USER,
    FORUM_ADD_QUESTION,
    FORUM_DEFAULT_TOPIC_NAME,
    FORUM_READ_MORE,
    HOMEPAGE_POST_TYPE_YOUTUBE_MK,
    HOME_FORUM_DISCUSSION_HINTS_FOOTER,
    HOME_FORUM_DISCUSSION_HINTS_TITLE,
    HOME_FORUM_TOPIC_HINTS_FOOTER,
    HOME_FORUM_TOPIC_HINTS_TITLE,
    JOIN,
    LATEST,
    NO_REPLIES,
    READ_OR_ASK,
    REPLY,
    REVIEW_VERIFIED,
    SEE_CONTESTS,
    SEO_TITLE_CONTESTS,
    SEO_TITLE_COUNSELLINGS,
    SEO_TITLE_FORUM,
    SHARE,
    SUBSCRIBE_TOPIC,
    TOPIC_COUNSELLOR,
    count_WIKI_ARTICLE_EXPERIENCES,
    counter_REPLIES,
} from "@/mk/autogenerated/translation";
import { WIKI_CATEGORIES } from "@/mk/autogenerated/wikiCategories";
import Breadcrumb from "@/mk/bazaar/common/components/Breadcrumb";
import { isAnonymousUsername } from "@/mk/bazaar/common/userUtils";
import { ACTIVE_SERVER_ID } from "@/mk/settings";

import styles from "./HomepagePost2.mscss";

const TEST_USER_ID = {
    201: 629922,
    202: 629922,
}[ACTIVE_SERVER_ID];

export const getBestVersionForPreview = getPhotoVersionFactory([
    "s720x720",
    "s600x600",
    "s560x560",
    "s1600x1600",
]);

export const getBestVersionForSmallPreview = getPhotoVersionFactory([
    "s150x150",
    "s720x720",
    "s600x600",
    "s560x560",
    "s1600x1600",
]);

const getResizedPhotoMaxWidth = (hpType) => {
    if (hpType === HomePostType.WIKI_EXPERIENCE) {
        return 200;
    }
    return 584;
};

const getResizedPhotoMaxHeight = (hpType) => {
    if (hpType === HomePostType.WIKI_EXPERIENCE) {
        return 200;
    }
    return 480;
};

interface OwnProps {
    className?: string;
    style?: React.CSSProperties;
    post: HomePostEntity;
    requestUser: UserEntity;
    isMobile: boolean;
    history?: History;

    onClick?(event: React.MouseEvent<HTMLAnchorElement>);
    onRendered?();
    onFollowUser(username: string, like: boolean);
    onSubscribeTopic(slug: string, username: string);
}

type Props = OwnProps;

interface BreadcrumbsAndCtaData {
    breadcrumbsData: BreadcrumbInput[];
    cta1label: string;
    cta2label: string;
    cta1link: string;
    cta2link: string;
    cta1onClick();
    cta2onClick();
}

function getBreadcrumbsAndCtaData(
    post: HomePostEntity,
    onFollowUserCallback,
    onSubscribeTopicCallback,
): BreadcrumbsAndCtaData {
    const breadcrumbsData: BreadcrumbInput[] = [];
    let cta1label = null;
    let cta1link = null;
    let cta1onClick = null;
    let cta2label = null;
    const cta2link = null;
    let cta2onClick = null;

    const username = post.user?.username;
    const showUsername = username && !isAnonymousUsername(username);

    if (post.type === HomePostType.YOUTUBE_MK) {
        breadcrumbsData.push({
            itemLabel: HOMEPAGE_POST_TYPE_YOUTUBE_MK,
            itemUrl: "https://www.youtube.com/c/Modrykonik",
        });
    } else if (post.type === HomePostType.FROM_INTERNET) {
        // no breadcrumbs
    } else if (
        post.type === HomePostType.PHOTOBLOG_ALBUM ||
        post.type === HomePostType.PHOTOBLOG_ARTICLE ||
        post.type === HomePostType.SHORT_MESSAGE
    ) {
        if (showUsername) {
            breadcrumbsData.push({ itemLabel: BLOGS, itemUrl: blogsUrl });
            breadcrumbsData.push({
                itemLabel: `@${username}`,
                itemUrl: url(blogsProfileUrl, { username }),
            });
        }
        cta2label = FOLLOW_USER;
        cta2onClick = onFollowUserCallback;
    } else if (post.type === HomePostType.CONTEST) {
        breadcrumbsData.push({ itemLabel: SEO_TITLE_CONTESTS, itemUrl: contestsHomeUrl });
        cta1label = SEE_CONTESTS;
        cta1link = contestsHomeUrl;
    } else if (
        post.type === HomePostType.FORUM_TOPIC ||
        post.type === HomePostType.DISCUSSION ||
        post.type === HomePostType.FORUM_COMMENT
    ) {
        if (post.topicName) {
            const topicName = post.topicName === FORUM_DEFAULT_TOPIC_NAME ? LATEST : post.topicName;
            breadcrumbsData.push({ itemLabel: SEO_TITLE_FORUM, itemUrl: forumUrl });
            breadcrumbsData.push({
                itemLabel: topicName,
                itemUrl: url(forumTopicUrl, { topicSlug: post.topicSlug }),
            });
        }
        cta1label = SUBSCRIBE_TOPIC;
        cta1onClick = onSubscribeTopicCallback;
    } else if (
        post.type === HomePostType.COUNSELLING ||
        post.type === HomePostType.COUNSELLING_TOPIC ||
        post.type === HomePostType.COUNSELLING_COMMENT
    ) {
        if (post.topicName) {
            breadcrumbsData.push({
                itemLabel: SEO_TITLE_COUNSELLINGS,
                itemUrl: counsellingsHomeUrl,
            });
            breadcrumbsData.push({
                itemLabel: post.topicName,
                itemUrl: url(counsellingsTopicUrl, { topicSlug: post.topicSlug }),
            });
        }
        cta1label = SUBSCRIBE_TOPIC;
        cta1onClick = onSubscribeTopicCallback;
    } else if (
        post.type === HomePostType.WIKI_EXPERIENCE ||
        post.type === HomePostType.WIKI_REVIEW_ARTICLE ||
        post.type === HomePostType.WIKI_ARTICLE
    ) {
        if (
            post.parentWikiArticleCategorySlug &&
            post.parentWikiArticleSlug &&
            post.parentWikiArticleTitle
        ) {
            const categoryName = WIKI_CATEGORIES.find(
                (cat) => cat.slug === post.parentWikiArticleCategorySlug,
            )?.name;
            if (categoryName) {
                breadcrumbsData.push({
                    itemLabel: categoryName,
                    itemUrl: getWikiArticleUrl(
                        post.parentWikiArticleCategorySlug,
                        post.parentWikiArticleCategorySlug,
                    ),
                });
                if (
                    post.parentWikiArticle2Title &&
                    post.parentWikiArticle2Slug &&
                    post.parentWikiArticle3Title &&
                    post.parentWikiArticle3Slug
                ) {
                    if (post.parentWikiArticle2Title && post.parentWikiArticle2Slug) {
                        breadcrumbsData.push({
                            itemLabel: post.parentWikiArticle2Title,
                            itemUrl: getWikiArticleUrl(
                                post.parentWikiArticleCategorySlug,
                                post.parentWikiArticle2Slug,
                            ),
                        });
                    }
                    if (post.parentWikiArticle3Title && post.parentWikiArticle3Slug) {
                        breadcrumbsData.push({
                            itemLabel: post.parentWikiArticle3Title,
                            itemUrl: getWikiArticleUrl(
                                post.parentWikiArticleCategorySlug,
                                post.parentWikiArticle3Slug,
                            ),
                        });
                    }
                } else {
                    breadcrumbsData.push({
                        itemLabel: post.parentWikiArticleTitle,
                        itemUrl: getWikiArticleUrl(
                            post.parentWikiArticleCategorySlug,
                            post.parentWikiArticleSlug,
                        ),
                    });
                }
            }
            cta1label = ADD_REVIEW;
            cta1link = url(wikiAddExperienceUrl, {
                articleSlug: post.parentWikiArticleSlug,
                categorySlug: post.parentWikiArticleCategorySlug,
            });
        }
    }

    return {
        breadcrumbsData,
        cta1label,
        cta1onClick,
        cta1link,
        cta2label,
        cta2onClick,
        cta2link,
    };
}

interface DiscussionHint {
    id: number;
    title: string;
    url: string;
    created: string;
    messageCount: number;
}

const HomepagePost2Component: React.FunctionComponent<Props> = ({
    className,
    onRendered,
    post,
    requestUser,
    onFollowUser,
    onSubscribeTopic,
    isMobile,
    history,
}) => {
    const rootRef = React.useRef<HTMLDivElement>(null);
    const calledRef = React.useRef(false);

    const onFollowUserCallback = React.useCallback(() => {
        if (post.user?.username) {
            onFollowUser(post.user.username, true);
        }
    }, [post.user?.id]);

    const [
        showTooltip,
        onProductMouseEnter,
        onProductMouseLeave,
        productRef,
        getProduct,
        bodyContainer,
    ] = useOverlay<HTMLDivElement>();

    const onSubscribeTopicCallback = React.useCallback(() => {
        if (requestUser) {
            onSubscribeTopic(post.topicSlug, requestUser.username);
        }
    }, [requestUser]);

    React.useEffect(() => {
        if (!onRendered) return;
        if (!rootRef.current) return;

        const observer = new window.IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry.isIntersecting && !calledRef.current) {
                    calledRef.current = true;
                    onRendered();
                    observer.disconnect();
                }
            },
            { threshold: 0.01 },
        );
        observer.observe(rootRef.current);

        return () => observer.disconnect();
    }, [onRendered]);

    const canShowShare = true;
    const canShowLikes = [
        HomePostType.PHOTOBLOG_ALBUM,
        HomePostType.PHOTOBLOG_ARTICLE,
        HomePostType.SHORT_MESSAGE,
        HomePostType.CONTEST,
        HomePostType.COUNSELLING_COMMENT,
        HomePostType.FORUM_TOPIC,
        HomePostType.DISCUSSION,
        HomePostType.FORUM_COMMENT,
        HomePostType.WIKI_REVIEW_ARTICLE,
        HomePostType.WIKI_ARTICLE,
        HomePostType.WIKI_EXPERIENCE,
    ].includes(post.type);
    const canShowReply = [
        HomePostType.PHOTOBLOG_ALBUM,
        HomePostType.PHOTOBLOG_ARTICLE,
        HomePostType.SHORT_MESSAGE,
        HomePostType.CONTEST,
        HomePostType.FORUM_TOPIC,
        HomePostType.DISCUSSION,
        HomePostType.FORUM_COMMENT,
    ].includes(post.type);
    const canShowAddQuestion =
        (post.type === HomePostType.COUNSELLING ||
            post.type === HomePostType.COUNSELLING_TOPIC ||
            post.type === HomePostType.COUNSELLING_COMMENT) &&
        post.topicSlug;
    const canShowRead = !canShowLikes && !canShowReply;

    const linkProps = {
        to:
            post.type === HomePostType.FORUM_COMMENT
                ? post.parentMessageUrl || post.repliedToMessageUrl || post.url
                : post.url,
        target:
            post.type === HomePostType.FROM_INTERNET || post.type === HomePostType.YOUTUBE_MK
                ? "_blank"
                : undefined,
        "data-cy": "home-post",
    };

    const { breadcrumbsData, cta1label, cta1onClick, cta1link, cta2label, cta2onClick, cta2link } =
        React.useMemo(() => {
            return getBreadcrumbsAndCtaData(post, onFollowUserCallback, onSubscribeTopicCallback);
        }, [post, requestUser]);

    const embeddedMediaElement = renderEmbeddedMedia(post);

    const debugElement = requestUser?.id === TEST_USER_ID && (
        <div
            style={{
                display: "inline-block",
                opacity: "0.2",
            }}
            ref={productRef}
            onMouseEnter={onProductMouseEnter}
            onMouseLeave={onProductMouseLeave}
        >
            &nbsp;...
            <OverlayLoadable
                placement="top"
                show={showTooltip}
                container={bodyContainer}
                target={getProduct}
            >
                {({ props, arrowProps, placement }) => (
                    <Tooltip
                        placement={placement}
                        containerRef={props.ref}
                        style={props.style}
                        arrowRef={arrowProps.ref}
                        arrowStyle={arrowProps.style}
                    >
                        <div
                            style={{
                                width: "100%",
                                height: "auto",
                                padding: "4px",
                                whiteSpace: "nowrap",
                            }}
                        >
                            <b>isHomepagePost</b>: {String(post.isHomepagePost)} <br />
                            <b>id</b>: {post.id} <br />
                            <b>objectId</b>: {String(post.objectId)} <br />
                            <b>entityName</b>: {String(post.entityName)}
                        </div>
                    </Tooltip>
                )}
            </OverlayLoadable>
        </div>
    );

    let userLine = null;
    let userLine1 = null;
    let userLine2 = null;
    if (post.counsellor) {
        userLine = (
            <>
                <div
                    className={cx(
                        styles.HomepagePost2__w100,
                        styles.HomepagePost2__mt5,
                        styles.HomepagePost2__counsellor,
                    )}
                >
                    <div className={styles.HomepagePost2__counsellorDescription}>
                        <ImgCropped
                            width={24}
                            height={24}
                            className={styles.HomepagePost2__counsellorAvatar}
                            photo={post.counsellor.avatarPhoto}
                            photoVersion={getBestVersionForSmallPreview(
                                post.counsellor.avatarPhoto,
                            )}
                            mode={ImgCropMode.Cover}
                        />
                        <div className={styles.HomepagePost2__counsellorName}>
                            {post.counsellor.name}
                        </div>
                        <div className={styles.HomepagePost2__counsellorBadge}>
                            {TOPIC_COUNSELLOR}
                        </div>
                    </div>
                    {post.publishTime && (
                        <>
                            <div className={styles.HomepagePost2__sep}>•</div>
                            <Link {...linkProps} className={styles.HomepagePost2__colorLabel}>
                                {timeAt(post.publishTime)}
                            </Link>
                        </>
                    )}
                </div>
            </>
        );
    } else if (post.user) {
        userLine = (
            <>
                <div
                    className={cx(
                        styles.HomepagePost2__w100,
                        styles.HomepagePost2__mt5,
                        styles.HomepagePost2__userAndPublishTime,
                    )}
                >
                    {post.user && (
                        <>
                            <UserChip
                                user={post.user}
                                anonymousUserPseudonym={post.anonymousUserPseudonym}
                                size={24}
                                showAvatar
                                spaceBetween={4}
                                boldUsername
                                showBadges
                            />
                        </>
                    )}

                    {post.publishTime && post.user && (
                        <div className={styles.HomepagePost2__sep}>•</div>
                    )}

                    {post.publishTime && (
                        <>
                            <Link {...linkProps} className={styles.HomepagePost2__colorLabel}>
                                {timeAt(post.publishTime)}
                            </Link>
                        </>
                    )}

                    {post.verificationStatus === ExperienceVerificationStatus.VERIFIED &&
                        post.user && <div className={styles.HomepagePost2__sep}>•</div>}

                    {post.verificationStatus === ExperienceVerificationStatus.VERIFIED && (
                        <>
                            <Link {...linkProps} className={styles.HomepagePost2__verified}>
                                <FontAwesomeIcon icon={faBadgeCheck} /> {REVIEW_VERIFIED}
                            </Link>
                        </>
                    )}

                    {cta2label && (
                        <>
                            <div className={styles.HomepagePost2__sep}>•</div>
                            <div className={styles.HomepagePost2__cta__buttonWrapper}>
                                <Btn
                                    label={cta2label}
                                    type={BtnType.BlueText}
                                    sidePadding={BtnSidePadding.None}
                                    link={cta2link}
                                    onClick={cta2onClick}
                                />
                            </div>
                        </>
                    )}
                </div>
            </>
        );
    }

    if ([HomePostType.COUNSELLING_COMMENT, HomePostType.FORUM_COMMENT].includes(post.type)) {
        userLine2 = userLine;
    } else {
        userLine1 = userLine;
    }

    if ([HomePostType.DISCUSSION_HINTS, HomePostType.TOPIC_HINTS].includes(post.type)) {
        const discussionHints = (
            post.snippetJson ? JSON.parse(post.snippetJson) : []
        ) as DiscussionHint[];

        return (
            <div className={cx(styles.HomepagePost2, styles.HomepagePost2__hints, className)}>
                <Panel
                    ref={rootRef}
                    className={cx(
                        styles.HomepagePost2__panel,
                        styles.HomepagePost2__colorPanel,
                        {
                            [HomePostType.DISCUSSION_HINTS]:
                                styles.HomepagePost2__types__hints__topBorder,
                            [HomePostType.TOPIC_HINTS]:
                                styles.HomepagePost2__types__hints__topBorder,
                        }[post.type],
                    )}
                >
                    <div className={styles.HomepagePost2__hints__header}>
                        {
                            {
                                [HomePostType.DISCUSSION_HINTS]: HOME_FORUM_DISCUSSION_HINTS_TITLE,
                                [HomePostType.TOPIC_HINTS]: HOME_FORUM_TOPIC_HINTS_TITLE,
                            }[post.type]
                        }
                    </div>

                    {discussionHints.map((hint) => {
                        const hintCtaButton = (
                            <Btn
                                type={BtnType.BlueOutline}
                                asAnchor
                                link={hint.url}
                                label={
                                    {
                                        [HomePostType.DISCUSSION_HINTS]: JOIN,
                                        [HomePostType.TOPIC_HINTS]: READ_OR_ASK,
                                    }[post.type]
                                }
                            />
                        );
                        return (
                            <div
                                key={hint.id}
                                className={cx(
                                    styles.HomepagePost2__w100,
                                    styles.HomepagePost2__mt10,
                                    styles.HomepagePost2__mb10,
                                    styles.HomepagePost2__hints__hint,
                                    {
                                        [HomePostType.DISCUSSION_HINTS]:
                                            styles.HomepagePost2__hints__discussionHint,
                                        [HomePostType.TOPIC_HINTS]:
                                            styles.HomepagePost2__hints__topicHint,
                                    }[post.type],
                                )}
                            >
                                <Link
                                    to={hint.url}
                                    className={cx(
                                        styles.HomepagePost2__hints__title,
                                        styles.HomepagePost2__block,
                                    )}
                                >
                                    <Clamp clamp={3}>
                                        <div>{hint.title}</div>
                                    </Clamp>
                                </Link>
                                {post.type === HomePostType.TOPIC_HINTS && hintCtaButton}
                                {post.type === HomePostType.DISCUSSION_HINTS && (
                                    <Link
                                        to={hint.url}
                                        className={cx(
                                            styles.HomepagePost2__hints__secondLine,
                                            styles.HomepagePost2__block,
                                        )}
                                    >
                                        <div className={cx(styles.HomepagePost2__hints__meta)}>
                                            {hint.messageCount !== null &&
                                                hint.messageCount !== undefined && (
                                                    <b>
                                                        {hint.messageCount !== 0
                                                            ? NO_REPLIES
                                                            : interpolate(counter_REPLIES, {
                                                                  counter: hint.messageCount,
                                                                  count: hint.messageCount,
                                                              })}
                                                    </b>
                                                )}
                                            {hint.created && (
                                                <>
                                                    <div className={styles.HomepagePost2__sep}>
                                                        •
                                                    </div>
                                                    {timeAt(hint.created)}
                                                </>
                                            )}
                                        </div>
                                        {hintCtaButton}
                                    </Link>
                                )}
                            </div>
                        );
                    })}
                    <Link
                        to={
                            {
                                [HomePostType.DISCUSSION_HINTS]: forumUrl,
                                [HomePostType.TOPIC_HINTS]: forumTopicsUrl,
                            }[post.type]
                        }
                        className={cx(
                            styles.HomepagePost2__hints__footer,
                            styles.HomepagePost2__block,
                        )}
                    >
                        {
                            {
                                [HomePostType.DISCUSSION_HINTS]: HOME_FORUM_DISCUSSION_HINTS_FOOTER,
                                [HomePostType.TOPIC_HINTS]: HOME_FORUM_TOPIC_HINTS_FOOTER,
                            }[post.type]
                        }
                    </Link>
                </Panel>
            </div>
        );
    } else {
        return (
            <div className={cx(styles.HomepagePost2, className)}>
                <Panel
                    ref={rootRef}
                    className={cx(
                        styles.HomepagePost2__panel,
                        styles.HomepagePost2__colorPanel,
                        {
                            [HomePostType.DISCUSSION_HINTS]:
                                styles.HomepagePost2__types__hints__topBorder,

                            [HomePostType.YOUTUBE_MK]:
                                styles.HomepagePost2__types__modrykonik__topBorder,
                            [HomePostType.FROM_INTERNET]:
                                styles.HomepagePost2__types__modrykonik__topBorder,
                            [HomePostType.WIKI_ARTICLE]:
                                styles.HomepagePost2__types__modrykonik__topBorder,

                            [HomePostType.PHOTOBLOG_ALBUM]:
                                styles.HomepagePost2__types__photoblogs__topBorder,
                            [HomePostType.PHOTOBLOG_ARTICLE]:
                                styles.HomepagePost2__types__photoblogs__topBorder,
                            [HomePostType.SHORT_MESSAGE]:
                                styles.HomepagePost2__types__photoblogs__topBorder,

                            [HomePostType.COUNSELLING]:
                                styles.HomepagePost2__types__counsellings__topBorder,
                            [HomePostType.COUNSELLING_TOPIC]:
                                styles.HomepagePost2__types__counsellings__topBorder,
                            [HomePostType.COUNSELLING_COMMENT]:
                                styles.HomepagePost2__types__counsellings__topBorder,

                            [HomePostType.CONTEST]:
                                styles.HomepagePost2__types__contests__topBorder,

                            [HomePostType.FORUM_TOPIC]:
                                styles.HomepagePost2__types__forum__topBorder,
                            [HomePostType.DISCUSSION]:
                                styles.HomepagePost2__types__forum__topBorder,
                            [HomePostType.FORUM_COMMENT]:
                                styles.HomepagePost2__types__forum__topBorder,

                            [HomePostType.WIKI_REVIEW_ARTICLE]:
                                styles.HomepagePost2__types__reviews__topBorder,
                            [HomePostType.WIKI_EXPERIENCE]:
                                styles.HomepagePost2__types__reviews__topBorder,
                        }[post.type],
                    )}
                >
                    {!!breadcrumbsData?.length && (
                        <div
                            className={cx(
                                styles.HomepagePost2__w100,
                                styles.HomepagePost2__mt5,
                                styles.HomepagePost2__mb5,
                                styles.HomepagePost2__breadcrumbsAndCta,
                            )}
                        >
                            <Breadcrumb
                                className={styles.HomepagePost2__breadcrumbs}
                                separator={
                                    <FontAwesomeIcon
                                        icon={faAngleRight}
                                        className={styles.HomepagePost2__sep}
                                    />
                                }
                            >
                                {breadcrumbsData.map((item, idx) => {
                                    return (
                                        <Link
                                            key={idx}
                                            to={item.itemUrl}
                                            className={cx(
                                                styles.HomepagePost2__breadcrumbs__item,
                                                idx === 0 &&
                                                    {
                                                        [HomePostType.YOUTUBE_MK]:
                                                            styles.HomepagePost2__types__modrykonik__breadcrumbRootItem,
                                                        [HomePostType.FROM_INTERNET]:
                                                            styles.HomepagePost2__types__modrykonik__breadcrumbRootItem,
                                                        [HomePostType.WIKI_ARTICLE]:
                                                            styles.HomepagePost2__types__modrykonik__breadcrumbRootItem,

                                                        [HomePostType.PHOTOBLOG_ALBUM]:
                                                            styles.HomepagePost2__types__photoblogs__breadcrumbRootItem,
                                                        [HomePostType.PHOTOBLOG_ARTICLE]:
                                                            styles.HomepagePost2__types__photoblogs__breadcrumbRootItem,
                                                        [HomePostType.SHORT_MESSAGE]:
                                                            styles.HomepagePost2__types__photoblogs__breadcrumbRootItem,

                                                        [HomePostType.COUNSELLING]:
                                                            styles.HomepagePost2__types__counsellings__breadcrumbRootItem,
                                                        [HomePostType.COUNSELLING_TOPIC]:
                                                            styles.HomepagePost2__types__counsellings__breadcrumbRootItem,
                                                        [HomePostType.COUNSELLING_COMMENT]:
                                                            styles.HomepagePost2__types__counsellings__breadcrumbRootItem,

                                                        [HomePostType.CONTEST]:
                                                            styles.HomepagePost2__types__contests__breadcrumbRootItem,

                                                        [HomePostType.FORUM_TOPIC]:
                                                            styles.HomepagePost2__types__forum__breadcrumbRootItem,
                                                        [HomePostType.DISCUSSION]:
                                                            styles.HomepagePost2__types__forum__breadcrumbRootItem,
                                                        [HomePostType.FORUM_COMMENT]:
                                                            styles.HomepagePost2__types__forum__breadcrumbRootItem,

                                                        [HomePostType.WIKI_REVIEW_ARTICLE]:
                                                            styles.HomepagePost2__types__reviews__breadcrumbRootItem,
                                                        [HomePostType.WIKI_EXPERIENCE]:
                                                            styles.HomepagePost2__types__reviews__breadcrumbRootItem,
                                                    }[post.type],
                                            )}
                                        >
                                            {item.itemLabel}
                                        </Link>
                                    );
                                })}
                            </Breadcrumb>
                            {cta1label && (
                                <div className={styles.HomepagePost2__cta}>
                                    <div className={styles.HomepagePost2__sep}>•</div>
                                    <div className={styles.HomepagePost2__cta__buttonWrapper}>
                                        <Btn
                                            label={cta1label}
                                            type={BtnType.BlueText}
                                            sidePadding={BtnSidePadding.None}
                                            onClick={cta1onClick}
                                            link={cta1link}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {post.type === HomePostType.WIKI_EXPERIENCE && (
                        <Link
                            className={cx(
                                styles.HomepagePost2__block,
                                styles.HomepagePost2__w100,
                                styles.HomepagePost2__mt5,
                                styles.HomepagePost2__mb5,
                                styles.HomepagePost2__experienceParentWikiArticle,
                            )}
                            to={getWikiArticleUrl(
                                post.parentWikiArticleCategorySlug,
                                post.parentWikiArticleSlug,
                            )}
                        >
                            {post.parentWikiArticlePhoto && (
                                <div
                                    className={
                                        styles.HomepagePost2__experienceParentWikiArticle__photo
                                    }
                                >
                                    <ImgCropped
                                        photo={post.parentWikiArticlePhoto}
                                        photoVersion={getBestVersionForSmallPreview(
                                            post.parentWikiArticlePhoto,
                                        )}
                                        lazy={false}
                                        mode={ImgCropMode.Cover}
                                        width={60}
                                        height={60}
                                    />
                                </div>
                            )}
                            <div
                                className={
                                    styles.HomepagePost2__experienceParentWikiArticle__details
                                }
                            >
                                <div
                                    className={
                                        styles.HomepagePost2__experienceParentWikiArticle__title
                                    }
                                >
                                    {post.parentWikiArticleTitle}
                                </div>
                                <div
                                    className={
                                        styles.HomepagePost2__experienceParentWikiArticle__meta
                                    }
                                >
                                    <span
                                        className={
                                            styles.HomepagePost2__experienceParentWikiArticle__star
                                        }
                                    >
                                        <Star isStarred onStarToggle={null} />
                                    </span>
                                    {!!post.parentWikiArticleScore && (
                                        <span
                                            className={
                                                styles.HomepagePost2__experienceParentWikiArticle__rating
                                            }
                                        >
                                            <span
                                                className={
                                                    styles.HomepagePost2__experienceParentWikiArticle__score
                                                }
                                            >
                                                {post.parentWikiArticleScore}
                                            </span>
                                            <span
                                                className={
                                                    styles.HomepagePost2__experienceParentWikiArticle__outOf
                                                }
                                            >
                                                &nbsp;/&nbsp;5
                                            </span>
                                        </span>
                                    )}
                                    <span className={styles.HomepagePost2__sep}>•</span>
                                    <span
                                        className={
                                            styles.HomepagePost2__experienceParentWikiArticle__count
                                        }
                                    >
                                        {interpolate(count_WIKI_ARTICLE_EXPERIENCES, {
                                            count: post.parentWikiArticleExpCount,
                                        })}
                                    </span>
                                </div>
                            </div>
                        </Link>
                    )}

                    {userLine1}

                    {post.starsCount !== null && post.starsCount !== undefined && (
                        <Link
                            {...linkProps}
                            className={cx(
                                styles.HomepagePost2__w100,
                                styles.HomepagePost2__mt5,
                                styles.HomepagePost2__block,
                            )}
                        >
                            <div className={styles.HomepagePost2__starsRating}>
                                <StarsRating starsCount={post.starsCount} />
                            </div>
                        </Link>
                    )}

                    {post.title && (
                        <Link
                            {...linkProps}
                            className={cx(
                                styles.HomepagePost2__w100,
                                styles.HomepagePost2__mt5,
                                styles.HomepagePost2__mb5,
                                styles.HomepagePost2__block,
                            )}
                        >
                            <div className={styles.HomepagePost2__title}>{post.title}</div>
                        </Link>
                    )}

                    {post.parentMessageBody && (
                        <div
                            className={cx(
                                styles.HomepagePost2__w100,
                                styles.HomepagePost2__mt5,
                                // styles.HomepagePost2__commentRepliedToMessage1,
                            )}
                        >
                            <CommentRepliedToMessage
                                clamp={3}
                                style={"twitter"}
                                repliedToMessageAuthor={post.parentMessageAuthor}
                                repliedToMessageBody={post.parentMessageBody}
                                hideTwitterLine={embeddedMediaElement || post.photo}
                                forumCommentUrl={post.parentMessageUrl}
                                history={history}
                                photo={
                                    !!post.parentMessagePhoto && (
                                        <div className={styles.HomepagePost2__mt5}>
                                            <ImgCropped
                                                photo={post.parentMessagePhoto}
                                                photoVersion={getBestVersionForSmallPreview(
                                                    post.parentMessagePhoto,
                                                )}
                                                lazy={false}
                                                mode={ImgCropMode.Cover}
                                                width={60}
                                                height={60}
                                            />
                                        </div>
                                    )
                                }
                            />
                        </div>
                    )}

                    {post.repliedToMessageBody && (
                        <div
                            className={cx(
                                styles.HomepagePost2__w100,
                                styles.HomepagePost2__mt5,
                                // post.parentMessageBody && styles.HomepagePost2__commentRepliedToMessage2,
                            )}
                        >
                            <CommentRepliedToMessage
                                clamp={2}
                                style={"twitter"}
                                repliedToMessageAuthor={post.repliedToMessageAuthor}
                                repliedToMessageBody={post.repliedToMessageBody}
                                hideTwitterLine={embeddedMediaElement || post.photo}
                                forumCommentUrl={post.repliedToMessageUrl}
                                history={history}
                                photo={
                                    !!post.repliedToMessagePhoto && (
                                        <div className={styles.HomepagePost2__mt5}>
                                            <ImgCropped
                                                photo={post.repliedToMessagePhoto}
                                                photoVersion={getBestVersionForSmallPreview(
                                                    post.repliedToMessagePhoto,
                                                )}
                                                lazy={false}
                                                mode={ImgCropMode.Cover}
                                                width={60}
                                                height={60}
                                            />
                                        </div>
                                    )
                                }
                            />
                        </div>
                    )}

                    {embeddedMediaElement && (
                        <div
                            className={cx(
                                styles.HomepagePost2__w100,
                                styles.HomepagePost2__mt5,
                                styles.HomepagePost2__mb5,
                                styles.HomepagePost2__embeddedMediaElement,
                            )}
                        >
                            {embeddedMediaElement}
                        </div>
                    )}

                    {!embeddedMediaElement && post.photo && (
                        <Link
                            {...linkProps}
                            className={cx(
                                styles.HomepagePost2__w100,
                                styles.HomepagePost2__mt5,
                                styles.HomepagePost2__mb5,
                                styles.HomepagePost2__block,
                            )}
                        >
                            <div
                                className={cx(
                                    styles.HomepagePost2__imageWrapper,
                                    post.type !== HomePostType.WIKI_EXPERIENCE &&
                                        styles.HomepagePost2__grayBg,
                                )}
                            >
                                <ResizedPhoto
                                    className={cx(
                                        post.type === HomePostType.WIKI_EXPERIENCE
                                            ? styles.HomepagePost2__imageOfExperience
                                            : styles.HomepagePost2__image,
                                    )}
                                    maxHeight={getResizedPhotoMaxHeight(post.type)}
                                    maxWidth={getResizedPhotoMaxWidth(post.type)}
                                    photo={post.photo}
                                    version={getBestVersionForPreview(post.photo)}
                                    enableResponsiveLayoutForSmallImages={false}
                                    enableResponsiveLayoutForLargeImages={isMobile}
                                    forceEager
                                />
                            </div>
                        </Link>
                    )}

                    {userLine2}

                    {post.description && (
                        <Link
                            {...linkProps}
                            className={cx(
                                styles.HomepagePost2__w100,
                                styles.HomepagePost2__mt5,
                                styles.HomepagePost2__mb5,
                                styles.HomepagePost2__block,
                            )}
                        >
                            <Clamp
                                clamp={3}
                                // endLabel={HOME_READ_MORE} TODO homepage2
                            >
                                <div className={cx(styles.HomepagePost2__description)}>
                                    {post.description}
                                </div>
                            </Clamp>
                        </Link>
                    )}

                    <div
                        className={cx(
                            styles.HomepagePost2__w100,
                            styles.HomepagePost2__mt5,
                            styles.HomepagePost2__footer,
                        )}
                    >
                        {canShowRead && (
                            <Link className={styles.HomepagePost2__footer__item} {...linkProps}>
                                {FORUM_READ_MORE}
                            </Link>
                        )}
                        {canShowLikes && post.objectId && post.entityName && (
                            <span
                                className={cx(
                                    styles.HomepagePost2__footer__item,
                                    styles["HomepagePost2__footer__item--ptpb0"],
                                )}
                            >
                                <Like
                                    id={post.objectId}
                                    entity={post.entityName}
                                    type={LikeType.IconCount}
                                    hideZero
                                />
                            </span>
                        )}
                        {canShowReply && post.comments !== null && post.comments !== undefined && (
                            <Link className={styles.HomepagePost2__footer__item} {...linkProps}>
                                <FontAwesomeIcon icon={faComment} />
                                &nbsp;
                                {post.comments}
                            </Link>
                        )}
                        {canShowAddQuestion && (
                            <Link
                                className={styles.HomepagePost2__footer__item}
                                {...linkProps}
                                to={url(counsellingsNewUrl, { topicSlug: post.topicSlug })}
                            >
                                <FontAwesomeIcon icon={faComment} />
                                &nbsp;
                                {FORUM_ADD_QUESTION}
                            </Link>
                        )}
                        {canShowShare && (
                            <Btn
                                label={
                                    <>
                                        {SHARE}
                                        {debugElement}
                                    </>
                                }
                                icon={faShare}
                                onClick={handleOnShare(post.url, post.title)}
                                type={BtnType.GreyText}
                                sidePadding={BtnSidePadding.None}
                                className={styles.HomepagePost2__footer__item}
                            />
                        )}
                    </div>
                </Panel>
            </div>
        );
    }
};

HomepagePost2Component.displayName = "HomepagePost2";

export const HomepagePost2 = React.memo<Props>(HomepagePost2Component);

// export default React.memo<Props>(Post);

interface PlaceholderOwnProps {
    className?: string;
    style?: React.CSSProperties;
}

let HomepagePost2Placeholder: React.FunctionComponent<PlaceholderOwnProps> = ({
    className,
    style,
}) => (
    <div className={cx(styles.HomepagePost2, className)}>
        <Panel
            style={style}
            className={cx(styles.HomepagePost2__placeholderPanel)}
            data-cy="home-post-placeholder"
        >
            <div className={styles.HomepagePost2__content}>
                <LoadingPlaceholder width={200} height={18} />
                <br />
                <LoadingPlaceholder width={80} height={80} />
            </div>
        </Panel>
    </div>
);

HomepagePost2Placeholder.displayName = "HomepagePost2Placeholder";

HomepagePost2Placeholder = React.memo<PlaceholderOwnProps>(HomepagePost2Placeholder);

export { HomepagePost2Placeholder };

export function outerHeightMax(isMobile: boolean): number {
    // Príklad: výška postu na mobile je menšia ako na desktope
    return isMobile ? 110 : 100;
}

const handleOnShare = (postUrl, title) => () => {
    if (url && title && navigator.share) {
        navigator
            .share({
                url: postUrl,
                title,
            })
            .then(() => {
                // alert('Zdieľanie bolo úspešné.');
            })
            .catch((error) => {
                if (!(error instanceof DOMException && error.name === "AbortError")) {
                    alert(`Chyba pri zdieľaní: ${error}`);
                }
            });
    } else {
        alert("Zdieľanie nie je na tomto zariadení podporované.");
    }
};

function renderEmbeddedMedia(post: any) {
    let element: any = null;

    let youtubeCode: string | null = null;
    let instagramPostCode: string | null = null;
    let instagramPostUrl: string | null = null;
    let tiktokPostUrl: string | null = null;
    let vimeoCode: string | null = null;

    if (post.snippetJson) {
        ({ youtubeCode, instagramPostCode, instagramPostUrl, tiktokPostUrl, vimeoCode } =
            extractFromSnippet(post.snippetJson));
    } else if (post.type === HomePostType.YOUTUBE_MK || post.type === HomePostType.FROM_INTERNET) {
        ({ youtubeCode, instagramPostCode, instagramPostUrl, tiktokPostUrl, vimeoCode } =
            extractFromUrl(post.url));
    }

    if (youtubeCode) {
        element = constructYoutube(youtubeCode, 560, 315, RenderSnippetAs.HTML_IFRAME);
    }
    if (instagramPostCode || instagramPostUrl) {
        element = constructInstagram(
            instagramPostCode,
            instagramPostUrl,
            RenderSnippetAs.HTML_IFRAME,
        );
    }
    if (tiktokPostUrl) {
        element = constructTiktok(tiktokPostUrl, RenderSnippetAs.HTML_IFRAME);
    }

    if (vimeoCode) {
        element = constructVimeo(vimeoCode, 560, 315, RenderSnippetAs.HTML_IFRAME);
    }

    return element;
}
